import { AfterViewInit, Component, ElementRef, OnDestroy, effect, inject, input, output, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { VisioComponentsModule } from '@visio-ai/ui-kit'
import { Kanban } from '../../../../../_services/kanban/kanban.model'
import {
  AdvancedFilterEntry,
  AdvancedFilters,
  Operator,
  OPERATOR_SYMBOLS
} from '../kanban-filters.model'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

dayjs.extend(customParseFormat)

const FIELD_TYPE_OPERATORS: Record<Kanban.Board.Field['type'], Operator[]> = {
  text: ['eq', 'neq'],
  number: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
  date: ['gte', 'lte', 'gt', 'lt', 'eq'],
  boolean: ['is'],
  user: ['eq', 'neq']
}

@Component({
  selector: 'app-kanban-filters-panel',
  standalone: true,
  imports: [FormsModule, VisioComponentsModule],
  templateUrl: './kanban-filters-panel.component.html',
  styleUrl: './kanban-filters-panel.component.scss'
})
export class KanbanFiltersPanelComponent implements AfterViewInit, OnDestroy {
  boardFields = input<Kanban.Board.Field[]>([])
  appliedFilters = input<AdvancedFilters>({})
  position = input<{ top: number; left: number }>({ top: 0, left: 0 })
  users = input<Partial<Record<string, { value: string; label: string }[]>>>({})

  apply = output<AdvancedFilters>()

  protected readonly operatorSymbols = OPERATOR_SYMBOLS

  protected drafts = signal<AdvancedFilters>({})

  private readonly el = inject(ElementRef)
  private calendarObserver?: MutationObserver

  constructor() {
    effect(() => {
      this.drafts.set({ ...this.appliedFilters() })
    })
  }

  ngAfterViewInit(): void {
    this.calendarObserver = new MutationObserver(() => {
      const calendar = this.el.nativeElement.querySelector('.calendar.show-calendar') as HTMLElement | null
      if (!calendar || calendar.style.position === 'fixed') return
      const container = calendar.closest('.container') as HTMLElement | null
      if (!container) return
      const rect = container.getBoundingClientRect()
      calendar.style.position = 'fixed'
      calendar.style.marginTop = '0'
      calendar.style.top = `${rect.bottom + 4}px`
      calendar.style.left = `${rect.left}px`
    })
    this.calendarObserver.observe(this.el.nativeElement, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    })
  }

  ngOnDestroy(): void {
    this.calendarObserver?.disconnect()
  }

  protected getOperators(field: Kanban.Board.Field): Operator[] {
    return FIELD_TYPE_OPERATORS[field.type]
  }

  protected getDraft(fieldId: string, field: Kanban.Board.Field): AdvancedFilterEntry {
    return this.drafts()[fieldId] ?? { operator: FIELD_TYPE_OPERATORS[field.type][0], value: '' }
  }

  protected onOperatorChange(fieldId: string, field: Kanban.Board.Field, operator: Operator): void {
    this.drafts.update(d => ({
      ...d,
      [fieldId]: { operator, value: d[fieldId]?.value ?? '' }
    }))
  }

  protected onValueChange(fieldId: string, field: Kanban.Board.Field, value: string): void {
    const operator = this.drafts()[fieldId]?.operator ?? FIELD_TYPE_OPERATORS[field.type][0]
    this.drafts.update(d => ({ ...d, [fieldId]: { operator, value } }))
  }

  protected onDateChange(fieldId: string, event: { date?: string; start?: string; end?: string }): void {
    if (!event.start || !event.end) return
    if (event.start === event.end) {
      const operator = this.drafts()[fieldId]?.operator ?? 'eq'
      this.drafts.update(d => ({ ...d, [fieldId]: { operator, value: event.start! } }))
    } else {
      this.drafts.update(d => ({ ...d, [fieldId]: { operator: 'gte', value: event.start!, rangeEnd: event.end! } }))
    }
  }

  protected isDateRangeDraft(fieldId: string): boolean {
    return !!this.drafts()[fieldId]?.rangeEnd
  }

  protected getDateDraftDisplay(fieldId: string): string {
    const draft = this.drafts()[fieldId]
    if (!draft?.value) return ''
    if (draft.rangeEnd) {
      const fmt = (iso: string) => {
        const d = dayjs(iso, 'YYYY-MM-DD', true)
        return d.isValid() ? d.format('DD/MM/YYYY') : iso
      }
      return `${fmt(draft.value)} - ${fmt(draft.rangeEnd)}`
    }
    const d = dayjs(draft.value, 'YYYY-MM-DD', true)
    return d.isValid() ? d.format('DD/MM/YYYY') : draft.value
  }

  protected applyFilters(): void {
    const result: AdvancedFilters = {}
    for (const [fieldId, draft] of Object.entries(this.drafts())) {
      if (draft.value) result[fieldId] = draft
    }
    this.apply.emit(result)
  }

  protected clearAll(): void {
    this.drafts.set({})
    this.apply.emit({})
  }

}
